import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { AppError } from '../api/request';
import { SUPPORTED_LOCALES, type LocaleCode } from '../config/i18n';
import Seo from './Seo';
import { panelPath } from '../config/paths';
import { postAuthPath } from '../onboarding/destination';

export default function Login() {
  const { t, i18n } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation() as { state?: { from?: { pathname: string } } };
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const current = SUPPORTED_LOCALES.includes(i18n.language as LocaleCode) ? i18n.language : 'tr';
  const locale: LocaleCode = current === 'en' ? 'en' : 'tr';
  const bullets = t('auth.login.bullets', { returnObjects: true }) as string[];

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const user = await login(email, password);
      const from = location.state?.from?.pathname;
      const dest = postAuthPath(user, from || panelPath('/'));
      navigate(dest, { replace: true });
    } catch (err) {
      const backendMessage = err instanceof AppError ? err.message : err instanceof Error ? err.message : '';
      setError(backendMessage || t('auth.login.genericError'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex flex-wrap bg-[#1c1420] text-[#f5eef7] font-sans antialiased">
      <Seo
        title={`${t('auth.login.title')} | Loopcast`}
        description={t('auth.login.subtitle')}
        locale={locale}
        canonicalPath="/login"
        noindex
      />
      {/* Left brand panel */}
      <div className="flex flex-[1_1_460px] min-w-[min(100%,380px)] flex-col justify-between border-r border-[#2b1f33] p-10 sm:p-14">
        <div className="flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 text-[#f5eef7] hover:opacity-90">
            <span className="flex h-[26px] w-[26px] items-center justify-center rounded-md bg-[#ff6f91] text-sm font-bold text-[#2b0e18]">
              L
            </span>
            <span className="text-lg font-bold tracking-tight">Loopcast</span>
          </Link>
          <div className="flex overflow-hidden rounded-md border border-[#3d2e46] text-xs font-semibold">
            {SUPPORTED_LOCALES.map((code) => {
              const active = current === code;
              return (
                <button
                  key={code}
                  type="button"
                  onClick={() => void i18n.changeLanguage(code)}
                  aria-pressed={active}
                  className={`px-2.5 py-1.5 uppercase transition-colors ${
                    active ? 'bg-[#ff6f91] text-[#2b0e18]' : 'bg-[#251b2c] text-[#c9b8d1] hover:text-[#f5eef7]'
                  }`}
                >
                  {code}
                </button>
              );
            })}
          </div>
        </div>

        <div className="my-12 max-w-[420px]">
          <h1 className="m-0 mb-4 text-[clamp(1.6rem,3vw,2.125rem)] font-extrabold leading-tight tracking-tight">
            {t('auth.login.heroTitle')}
          </h1>
          <p className="mb-7 text-[15px] leading-relaxed text-[#c9b8d1]">{t('auth.login.heroSub')}</p>
          <ul className="flex flex-col gap-3.5">
            {(Array.isArray(bullets) ? bullets : []).map((b) => (
              <li key={b} className="flex items-start gap-2.5 text-sm text-[#e6dbe9]">
                <Check className="mt-0.5 h-[18px] w-[18px] shrink-0 stroke-[2.5] text-[#ff6f91]" aria-hidden />
                {b}
              </li>
            ))}
          </ul>
        </div>

        <blockquote className="max-w-[380px] rounded-xl border border-[#3d2e46] bg-[#221733] p-5">
          <p className="m-0 mb-3 text-[13.5px] leading-relaxed text-[#f5eef7]">
            “{t('auth.login.quote')}”
          </p>
          <div className="flex items-center gap-2.5">
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#ff9a6c] text-xs font-bold text-[#2b0e18]">
              E
            </span>
            <cite className="not-italic text-[12.5px] font-semibold text-[#c9b8d1]">
              {t('auth.login.quoteAuthor')}
            </cite>
          </div>
        </blockquote>
      </div>

      {/* Right form panel */}
      <div className="flex flex-[1_1_420px] min-w-[min(100%,320px)] items-center justify-center p-8 sm:p-10">
        <div className="w-full max-w-[400px]">
          <h2 className="m-0 mb-2 text-2xl font-extrabold tracking-tight">{t('auth.login.title')}</h2>
          <p className="mb-6 text-[13.5px] text-[#c9b8d1]">{t('auth.login.subtitle')}</p>

          <button
            type="button"
            className="mb-[18px] flex w-full items-center justify-center gap-2.5 rounded-lg border border-[#3d2e46] bg-[#251b2c] px-3 py-3 text-sm font-semibold text-[#f5eef7] transition-colors hover:border-[#ff6f91]"
          >
            <span
              className="h-[18px] w-[18px] rounded bg-[repeating-linear-gradient(135deg,#3d2e46,#3d2e46_3px,#2b1f33_3px,#2b1f33_6px)]"
              aria-hidden
            />
            {t('auth.login.googleBtn')}
          </button>

          <div className="mb-[18px] flex items-center gap-3">
            <div className="h-px flex-1 bg-[#3d2e46]" />
            <span className="text-xs text-[#a8899f]">{t('auth.login.orEmail')}</span>
            <div className="h-px flex-1 bg-[#3d2e46]" />
          </div>

          <form onSubmit={onSubmit} className="mb-4 flex flex-col gap-3.5">
            <div>
              <label htmlFor="login-email" className="mb-1.5 block text-[12.5px] font-semibold text-[#c9b8d1]">
                {t('auth.email')}
              </label>
              <input
                id="login-email"
                type="email"
                required
                autoComplete="email"
                placeholder={t('auth.login.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-lg border border-[#3d2e46] bg-[#1c1420] px-3.5 py-2.5 text-sm text-[#f5eef7] placeholder:text-[#6b5a72] outline-none transition-colors focus:border-[#ff6f91]"
              />
            </div>
            <div>
              <div className="mb-1.5 flex items-center justify-between">
                <label htmlFor="login-password" className="text-[12.5px] font-semibold text-[#c9b8d1]">
                  {t('auth.password')}
                </label>
                <a href="#" className="text-xs text-[#ff9a6c] hover:text-[#ffb088]">
                  {t('auth.login.forgotLabel')}
                </a>
              </div>
              <div className="relative">
                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  autoComplete="current-password"
                  placeholder={t('auth.login.passwordPlaceholder')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-lg border border-[#3d2e46] bg-[#1c1420] py-2.5 pl-3.5 pr-12 text-sm text-[#f5eef7] placeholder:text-[#6b5a72] outline-none transition-colors focus:border-[#ff6f91]"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 px-1 text-xs font-semibold text-[#a8899f] hover:text-[#f5eef7]"
                >
                  {showPassword ? t('auth.login.hidePassword') : t('auth.login.showPassword')}
                </button>
              </div>
            </div>

            <label className="mt-1 flex cursor-pointer items-center gap-2.5 text-[12.5px] text-[#c9b8d1]">
              <input
                type="checkbox"
                checked={remember}
                onChange={() => setRemember((v) => !v)}
                className="accent-[#ff6f91]"
              />
              {t('auth.login.rememberLabel')}
            </label>

            {error && <p className="text-sm text-[#ff8a9e]">{error}</p>}

            <button
              type="submit"
              disabled={submitting}
              className="mt-1 w-full rounded-lg border-none bg-[#ff6f91] py-3 text-[14.5px] font-bold text-[#2b0e18] transition-colors hover:bg-[#ff8aa5] disabled:cursor-not-allowed disabled:opacity-70"
            >
              {submitting ? t('auth.login.submitting') : t('auth.login.submit')}
            </button>
          </form>

          <p className="mt-5 text-center text-[13px] text-[#c9b8d1]">
            {t('auth.login.noAccount')}{' '}
            <Link to="/register" className="font-medium text-[#ff9a6c] hover:text-[#ffb088]">
              {t('auth.login.registerLink')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
